import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  query,
  where,
  onSnapshot,
  orderBy,
  Timestamp,
  getDocs,
  setDoc,
  getDoc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '@/lib/firebase';
import type { SMContact, BankerDocument } from '@/types';
import { normalizePhoneNumber } from '@/lib/constants';

// Collection references
const bankersCollection = collection(db, 'bankers');
const bankerFavoritesCollection = collection(db, 'bankerFavorites');

// Helper to safely convert any date format to Date object
function toDate(val: any): Date {
  if (!val) return new Date();
  if (val instanceof Date) return val;
  if (typeof val.toDate === 'function') return val.toDate(); // Firestore Timestamp
  if (typeof val === 'number') return new Date(val);
  if (typeof val === 'string') return new Date(val);
  return new Date();
}

/**
 * Upload a document to Firebase Storage
 */
export const uploadBankerDocument = async (
  file: File,
  bankerId: string
): Promise<BankerDocument> => {
  try {
    const timestamp = Date.now();
    const uniqueFilename = `${timestamp}-${file.name}`;
    
    // Storage path: bankers/{bankerId}/{filename}
    const storagePath = `bankers/${bankerId}/${uniqueFilename}`;
    const storageRef = ref(storage, storagePath);
    
    // Upload file
    const uploadResult = await uploadBytes(storageRef, file);
    
    // Get download URL
    const downloadURL = await getDownloadURL(uploadResult.ref);
    
    // Return document object
    const document: BankerDocument = {
      id: `doc_${timestamp}_${Math.random()}_${file.name}`,
      name: file.name,
      type: file.type,
      size: file.size,
      url: downloadURL,
    };
    
    return document;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw new Error(`Failed to upload file: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Delete a document from Firebase Storage
 */
export const deleteBankerDocument = async (fileUrl: string): Promise<void> => {
  try {
    // Skip if it's a base64 data URL
    if (fileUrl.startsWith('data:')) {
      return;
    }
    
    // Extract storage path from download URL
    const url = new URL(fileUrl);
    const pathMatch = url.pathname.match(/\/o\/(.+)$/);
    
    if (!pathMatch) {
      console.warn('Could not extract storage path from URL:', fileUrl);
      return;
    }
    
    const storagePath = decodeURIComponent(pathMatch[1]);
    const storageRef = ref(storage, storagePath);
    
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting file:', error);
    throw new Error(`Failed to delete file: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Recursively remove undefined values from an object
 */
const removeUndefinedDeep = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefinedDeep(item)).filter(item => item !== undefined);
  }
  
  if (typeof obj === 'object' && !(obj instanceof Date) && !(obj instanceof Timestamp)) {
    const cleaned: Record<string, any> = {};
    for (const key of Object.keys(obj)) {
      const value = obj[key];
      if (value !== undefined) {
        cleaned[key] = removeUndefinedDeep(value);
      }
    }
    return cleaned;
  }
  
  return obj;
};

/**
 * Convert banker data to Firestore format
 */
const convertBankerToFirestore = (banker: Partial<SMContact> & { updatedAt?: Date }) => {
  // First, deep clean undefined values
  const cleanedBanker = removeUndefinedDeep(banker);
  
  const firestoreBanker: Record<string, unknown> = { ...cleanedBanker };
  
  // Convert Date objects to Firestore Timestamps
  if (cleanedBanker.createdAt) {
    firestoreBanker.createdAt = Timestamp.fromDate(cleanedBanker.createdAt instanceof Date ? cleanedBanker.createdAt : new Date(cleanedBanker.createdAt));
  }
  if (cleanedBanker.updatedAt) {
    firestoreBanker.updatedAt = Timestamp.fromDate(cleanedBanker.updatedAt instanceof Date ? cleanedBanker.updatedAt : new Date(cleanedBanker.updatedAt));
  }
  
  // Remove fields that shouldn't be in Firestore
  delete firestoreBanker.id;

  // Remove empty-string optional fields — Firestore rules reject empty email
  // (rule: if email key exists, it must pass isValidEmail regex)
  const optionalStringFields = ['email', 'designation', 'region', 'remark'];
  for (const field of optionalStringFields) {
    if (firestoreBanker[field] === '' || firestoreBanker[field] === null) {
      delete firestoreBanker[field];
    }
  }

  return firestoreBanker;
};

/**
 * Convert Firestore data to banker object
 */
const convertFirestoreToBanker = (id: string, data: any): SMContact => {
  // Clean documents array to ensure all required fields are present
  const documents = data.documents?.filter((doc: any) => 
    doc && 
    typeof doc.id === 'string' && 
    typeof doc.name === 'string' && 
    typeof doc.type === 'string' && 
    typeof doc.size === 'number'
  ) || [];

  return {
    ...data,
    id,
    // Normalise to string — Firestore docs from the dashboard app can store these
    // as non-string types (e.g. numbers or null), which causes crashes in UI
    // code that calls .substring()/.trim() on them.
    name: typeof data.name === 'string' ? data.name : String(data.name ?? ''),
    bank: typeof data.bank === 'string' ? data.bank
        : typeof data.bankName === 'string' ? data.bankName
        : String(data.bank ?? data.bankName ?? ''),
    phone: typeof data.phone === 'string' ? data.phone : String(data.phone ?? ''),
    // Normalise to array — Firestore docs from the dashboard app can store this
    // as a comma-separated string, which crashes UI code that calls .slice()/.map() on it.
    products: Array.isArray(data.products)
      ? data.products
      : typeof data.products === 'string' && data.products.length > 0
        ? data.products.split(',').map((p: string) => p.trim()).filter(Boolean)
        : [],
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
    documents,
  };
};

/**
 * Check if a banker with the given phone number already exists
 */
export const checkBankerExists = async (phone: string, excludeId?: string): Promise<boolean> => {
  try {
    const normalizedPhone = normalizePhoneNumber(phone);
    
    const q = query(
      bankersCollection,
      where('phone', '==', normalizedPhone)
    );
    
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return false;
    }
    
    // If excludeId is provided, check if the found banker is not the same one
    if (excludeId) {
      return snapshot.docs.some(doc => doc.id !== excludeId);
    }
    
    return true;
  } catch (error) {
    console.error('Error checking banker existence:', error);
    throw error;
  }
};

/**
 * Subscribe to all bankers (shared across all users)
 */
export const subscribeToBankers = (
  callback: (bankers: SMContact[]) => void
): (() => void) => {
  const q = query(
    bankersCollection,
    orderBy('createdAt', 'desc')
  );

  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      const bankers = snapshot.docs
        .filter(doc => doc.id !== '__table_meta__' && doc.id !== '__columns__') // skip dashboard metadata docs
        .map(doc => convertFirestoreToBanker(doc.id, doc.data()))
        .filter(b => b.name && b.name.trim().length > 0); // drop nameless/blank-name rows
      callback(bankers);
    },
    (error) => {
      console.error('Error subscribing to bankers:', error);
    }
  );

  return unsubscribe;
};

/**
 * Add a new banker
 */
export const addBanker = async (banker: Omit<SMContact, 'id'>): Promise<SMContact> => {
  try {
    // Normalize phone number before adding
    const normalizedPhone = normalizePhoneNumber(banker.phone);
    
    const bankerData = convertBankerToFirestore({
      ...banker,
      phone: normalizedPhone,
      createdAt: new Date(),
    });

    const docRef = await addDoc(bankersCollection, bankerData);
    
    return {
      ...banker,
      phone: normalizedPhone,
      id: docRef.id,
      createdAt: new Date(),
    };
  } catch (error) {
    console.error('Error adding banker:', error);
    throw error;
  }
};

/**
 * Update an existing banker
 */
export const updateBanker = async (bankerId: string, updates: Partial<SMContact>): Promise<void> => {
  try {
    // Normalize phone number if it's being updated
    const updatesToApply = { ...updates };
    if (updates.phone) {
      updatesToApply.phone = normalizePhoneNumber(updates.phone);
    }
    
    const bankerRef = doc(db, 'bankers', bankerId);
    const updateData = convertBankerToFirestore({
      ...updatesToApply,
      updatedAt: new Date(),
    } as Partial<SMContact> & { updatedAt: Date });

    await updateDoc(bankerRef, updateData);
  } catch (error) {
    console.error('Error updating banker:', error);
    throw error;
  }
};

/**
 * Delete a banker
 */
export const deleteBanker = async (bankerId: string): Promise<void> => {
  try {
    // Get the banker to retrieve documents
    const bankerRef = doc(db, 'bankers', bankerId);
    const bankerDoc = await getDoc(bankerRef);
    
    if (bankerDoc.exists()) {
      const banker = convertFirestoreToBanker(bankerDoc.id, bankerDoc.data());
      
      // Delete all associated documents from storage
      if (banker.documents && banker.documents.length > 0) {
        for (const document of banker.documents) {
          if (document.url) {
            try {
              await deleteBankerDocument(document.url);
            } catch (error) {
              console.error('Error deleting document:', error);
            }
          }
        }
      }
    }
    
    // Delete the banker document
    await deleteDoc(bankerRef);
  } catch (error) {
    console.error('Error deleting banker:', error);
    throw error;
  }
};

/**
 * Check if a banker is favorited by a specific user
 */
export const isBankerFavorited = async (userId: string, bankerId: string): Promise<boolean> => {
  try {
    const favoriteRef = doc(db, 'bankerFavorites', `${userId}_${bankerId}`);
    const favoriteDoc = await getDoc(favoriteRef);
    return favoriteDoc.exists();
  } catch (error) {
    console.error('Error checking favorite status:', error);
    return false;
  }
};

/**
 * Subscribe to user's favorite bankers
 */
export const subscribeToFavoriteBankers = (
  userId: string,
  callback: (bankerIds: string[]) => void
): (() => void) => {
  const q = query(
    bankerFavoritesCollection,
    where('userId', '==', userId)
  );

  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      const bankerIds = snapshot.docs.map(doc => doc.data().bankerId as string);
      callback(bankerIds);
    },
    (error) => {
      console.error('Error subscribing to favorite bankers:', error);
    }
  );

  return unsubscribe;
};

/**
 * Toggle favorite status for a banker
 */
export const toggleBankerFavorite = async (userId: string, bankerId: string): Promise<boolean> => {
  try {
    const favoriteRef = doc(db, 'bankerFavorites', `${userId}_${bankerId}`);
    const favoriteDoc = await getDoc(favoriteRef);
    
    if (favoriteDoc.exists()) {
      // Remove from favorites
      await deleteDoc(favoriteRef);
      return false;
    } else {
      // Add to favorites
      await setDoc(favoriteRef, {
        userId,
        bankerId,
        createdAt: Timestamp.fromDate(new Date()),
      });
      return true;
    }
  } catch (error) {
    console.error('Error toggling favorite:', error);
    throw error;
  }
};
